'use client';

import { useEffect, useMemo } from 'react';

import { useAuiState } from '@assistant-ui/react';

import {
  parseStepTimings,
  type PersistedStepTimings,
} from '@/components/assistant-ui/thread/trail-timing';

interface MetadataTrailTimings {
  /** Parsed `activityTrailStepTimings` from the message metadata, or undefined. */
  stepTimings: PersistedStepTimings | undefined;
  /** Parsed `activityTrailDurationMs` from the message metadata, or undefined. */
  durationMs: number | undefined;
}

function readCustomMetadata(message: unknown): Record<string, unknown> | undefined {
  return (message as { metadata?: { custom?: Record<string, unknown> } } | undefined)?.metadata
    ?.custom;
}

function readDurationMs(message: unknown): number | undefined {
  const dur = readCustomMetadata(message)?.activityTrailDurationMs;
  return typeof dur === 'number' && Number.isFinite(dur) && dur >= 0 ? dur : undefined;
}

// JSON-stringified so `useSyncExternalStore` doesn't trip its identity-stability guard.
function readStepTimingsJson(message: unknown): string | undefined {
  const timings = readCustomMetadata(message)?.activityTrailStepTimings;
  if (!timings || typeof timings !== 'object') return undefined;
  try {
    return JSON.stringify(timings);
  } catch {
    return undefined;
  }
}

/**
 * Read-only-host fallback (e.g. bazak-os-platform): trail timings come in
 * via `message.metadata.custom.activityTrail*`. Warns in debug mode if the
 * metadata is malformed.
 */
export function useMetadataTrailTimings({
  isDebugMode,
}: {
  isDebugMode: boolean;
}): MetadataTrailTimings {
  const durationMs = useAuiState(({ message }) => readDurationMs(message));
  const stepTimingsJson = useAuiState(({ message }) => readStepTimingsJson(message));

  const { stepTimings, parseFailure } = useMemo<{
    stepTimings: PersistedStepTimings | undefined;
    parseFailure: { reason: 'parse'; error: unknown } | { reason: 'shape'; raw: unknown } | null;
  }>(() => {
    if (!stepTimingsJson) return { stepTimings: undefined, parseFailure: null };
    let parsed: unknown;
    try {
      parsed = JSON.parse(stepTimingsJson);
    } catch (err) {
      return { stepTimings: undefined, parseFailure: { reason: 'parse', error: err } };
    }
    const validated = parseStepTimings(parsed);
    if (!validated)
      return { stepTimings: undefined, parseFailure: { reason: 'shape', raw: parsed } };
    return { stepTimings: validated, parseFailure: null };
  }, [stepTimingsJson]);

  useEffect(() => {
    if (!isDebugMode || !parseFailure) return;
    if (parseFailure.reason === 'parse') {
      console.warn('[activity-trail] failed to parse activityTrailStepTimings', parseFailure.error);
    } else {
      console.warn('[activity-trail] activityTrailStepTimings shape was invalid', parseFailure.raw);
    }
  }, [isDebugMode, parseFailure]);

  return { stepTimings, durationMs };
}
